import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from '@material-ui/core';

import { getAllSupermarkets } from '../../../requests/api/supermarkets';
import { getProductsPerPage } from '../../../requests/api/products';
import { postCreateComparativeReport } from '../../../requests/api/reports';
import { postCreateEmailReport } from '../../../requests/api/emailreports';

import useStyles from './styles';

function ReportCreateForm() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const account = useSelector((state) => state.account);

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [supermarkets, setSupermarkets] = useState([]);
  const [selectedSupermarket, setSelectedSupermarket] = useState('');

  // Product search state - indexed by supermarketId and baseIndex
  // Format: { `${supermarketId}-${baseIndex}`: value }
  const [searchTerms, setSearchTerms] = useState({});
  const [allProductsForBase, setAllProductsForBase] = useState({});
  const [isSearching, setIsSearching] = useState({});
  const [productPages, setProductPages] = useState({});
  const [productLimits, setProductLimits] = useState({});
  const [totalProducts, setTotalProducts] = useState({});

  // Helper function to get key for search state
  const getSearchKey = (supermarketId, baseIndex) => `${supermarketId}-${baseIndex}`;
  const [compareProductPage, setCompareProductPage] = useState(0);
  const [compareProductLimit, setCompareProductLimit] = useState(10);

  // Refs for scroll animation
  const compareProductsRef = useRef({});

  // Base products structure: { supermarketId: [{ baseProduct, compareProducts: [] }] }
  const [baseProducts, setBaseProducts] = useState({});

  const [openPeriodicSend, setOpenPeriodicSend] = useState(false);

  useEffect(() => {
    const fetchSupermarkets = async () => {
      const response = await getAllSupermarkets();
      if (response.success && response.data?.data) {
        const supermarketsData = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data.supermarkets || [];
        setSupermarkets(supermarketsData);
      }
    };
    fetchSupermarkets();
  }, []);

  const loadProductsForBase = async (supermarketId, baseIndex, page = 0, limit = 1000, searchValue = '') => {
    if (!supermarketId) return;

    const key = getSearchKey(supermarketId, baseIndex);
    setIsSearching((prev) => ({ ...prev, [key]: true }));
    try {
      const userRole = account.user.role;
      const userId = account.user.id;
      // Load all products for comparison (limit 1000 for now)
      const response = await getProductsPerPage(
        page,
        limit,
        searchValue,
        'ALPH',
        supermarketId.toString(),
        '',
        '',
        '',
        userRole,
        userId,
        false,
      );

      if (response.success && response.data?.data) {
        const products = response.data.data.products || [];
        const total = response.data.data.totalProducts || 0;
        setAllProductsForBase((prev) => ({ ...prev, [key]: products }));
        setTotalProducts((prev) => ({ ...prev, [key]: total }));
      }
    } catch (error) {
      enqueueSnackbar('Error al cargar productos', { variant: 'error' });
    } finally {
      setIsSearching((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSupermarketChange = async (supermarketId) => {
    setSelectedSupermarket(supermarketId);

    // Initialize base products for this supermarket if not exists
    if (!baseProducts[supermarketId]) {
      setBaseProducts((prev) => ({
        ...prev,
        [supermarketId]: [],
      }));
    }

    // Load products for existing base products of this supermarket
    const existingBaseProducts = baseProducts[supermarketId] || [];
    existingBaseProducts.forEach((baseData, index) => {
      const key = getSearchKey(supermarketId, index);
      // Only load if products haven't been loaded yet
      if (!allProductsForBase[key] || allProductsForBase[key].length === 0) {
        const searchTerm = searchTerms[key] || '';
        loadProductsForBase(supermarketId, index, 0, 1000, searchTerm);
      }
    });
  };

  const handleSearchChange = (baseIndex, value) => {
    if (!selectedSupermarket) return;
    const key = getSearchKey(selectedSupermarket, baseIndex);
    setSearchTerms((prev) => ({ ...prev, [key]: value }));
    setProductPages((prev) => ({ ...prev, [key]: 0 }));
    loadProductsForBase(selectedSupermarket, baseIndex, 0, 1000, value);
  };

  const handleProductPageChange = (baseIndex, event, newPage) => {
    if (!selectedSupermarket) return;
    const key = getSearchKey(selectedSupermarket, baseIndex);
    setProductPages((prev) => ({ ...prev, [key]: newPage }));
  };

  const handleProductLimitChange = (baseIndex, event) => {
    if (!selectedSupermarket) return;
    const key = getSearchKey(selectedSupermarket, baseIndex);
    const newLimit = parseInt(event.target.value, 10);
    setProductLimits((prev) => ({ ...prev, [key]: newLimit }));
    setProductPages((prev) => ({ ...prev, [key]: 0 }));
  };

  const handleCompareProductPageChange = (event, newPage) => {
    setCompareProductPage(newPage);
  };

  const handleCompareProductLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setCompareProductLimit(newLimit);
    setCompareProductPage(0);
  };

  // Get paginated products for base product selection
  const getPaginatedProducts = (baseIndex) => {
    if (!selectedSupermarket) return [];
    const key = getSearchKey(selectedSupermarket, baseIndex);
    const products = allProductsForBase[key] || [];
    const page = productPages[key] || 0;
    const limit = productLimits[key] || 10;
    const start = page * limit;
    const end = start + limit;
    return products.slice(start, end);
  };

  // Get paginated products for comparison (excluding base product)
  const getPaginatedCompareProducts = (baseIndex, baseProductId) => {
    if (!selectedSupermarket) return [];
    const key = getSearchKey(selectedSupermarket, baseIndex);
    const products = allProductsForBase[key] || [];
    const filtered = products.filter((p) => p.id !== baseProductId);
    const start = compareProductPage * compareProductLimit;
    const end = start + compareProductLimit;
    return filtered.slice(start, end);
  };

  const getTotalCompareProducts = (baseIndex, baseProductId) => {
    if (!selectedSupermarket) return 0;
    const key = getSearchKey(selectedSupermarket, baseIndex);
    const products = allProductsForBase[key] || [];
    return products.filter((p) => p.id !== baseProductId).length;
  };

  const handleAddBaseProduct = () => {
    if (!selectedSupermarket) {
      enqueueSnackbar('Debe seleccionar un supermercado primero', { variant: 'warning' });
      return;
    }

    const currentBaseProducts = baseProducts[selectedSupermarket] || [];
    const newIndex = currentBaseProducts.length;
    const key = getSearchKey(selectedSupermarket, newIndex);

    // Initialize new base product entry
    setBaseProducts((prev) => ({
      ...prev,
      [selectedSupermarket]: [
        ...(prev[selectedSupermarket] || []),
        {
          baseProduct: null,
          compareProducts: [],
        },
      ],
    }));

    // Initialize search state for new base product
    setSearchTerms((prev) => ({ ...prev, [key]: '' }));
    setProductPages((prev) => ({ ...prev, [key]: 0 }));
    setProductLimits((prev) => ({ ...prev, [key]: 10 }));
    setAllProductsForBase((prev) => ({ ...prev, [key]: [] }));
    setTotalProducts((prev) => ({ ...prev, [key]: 0 }));

    // Load initial products for the new base product
    loadProductsForBase(selectedSupermarket, newIndex, 0, 1000, '');
  };

  const handleSelectBaseProduct = (supermarketId, index, product) => {
    setBaseProducts((prev) => {
      const newProducts = [...(prev[supermarketId] || [])];
      newProducts[index] = {
        ...newProducts[index],
        baseProduct: product,
      };
      return {
        ...prev,
        [supermarketId]: newProducts,
      };
    });

    // Load products for this base if not already loaded
    const key = getSearchKey(supermarketId, index);
    if (!allProductsForBase[key] || allProductsForBase[key].length === 0) {
      const searchTerm = searchTerms[key] || '';
      loadProductsForBase(supermarketId, index, 0, 1000, searchTerm);
    }
  };

  const handleToggleCompareProduct = (supermarketId, baseIndex, product) => {
    setBaseProducts((prev) => {
      const newProducts = [...(prev[supermarketId] || [])];
      const currentBase = newProducts[baseIndex];
      const baseProduct = currentBase?.baseProduct;

      if (!baseProduct) {
        enqueueSnackbar('Debe seleccionar un producto base primero', { variant: 'warning' });
        return prev;
      }

      const compareProducts = currentBase?.compareProducts || [];
      const isSelected = compareProducts.some((p) => p.id === product.id);

      if (isSelected) {
        newProducts[baseIndex] = {
          ...currentBase,
          compareProducts: compareProducts.filter((p) => p.id !== product.id),
        };
      } else {
        // Don't allow selecting the base product as a compare product
        if (product.id === baseProduct.id) {
          enqueueSnackbar('No puede comparar un producto consigo mismo', { variant: 'warning' });
          return prev;
        }
        newProducts[baseIndex] = {
          ...currentBase,
          compareProducts: [...compareProducts, product],
        };
      }

      return {
        ...prev,
        [supermarketId]: newProducts,
      };
    });
  };

  const handleRemoveBaseProduct = (supermarketId, index) => {
    setBaseProducts((prev) => {
      const newProducts = [...(prev[supermarketId] || [])];
      newProducts.splice(index, 1);
      return {
        ...prev,
        [supermarketId]: newProducts,
      };
    });

    // Clean up search state for removed base product
    const removedKey = getSearchKey(supermarketId, index);
    setSearchTerms((prev) => {
      const newTerms = { ...prev };
      delete newTerms[removedKey];
      // Reindex remaining terms for this supermarket
      const reindexed = { ...newTerms };
      const currentBaseProducts = baseProducts[supermarketId] || [];
      for (let i = index + 1; i < currentBaseProducts.length; i += 1) {
        const oldKey = getSearchKey(supermarketId, i);
        const newKey = getSearchKey(supermarketId, i - 1);
        if (reindexed[oldKey]) {
          reindexed[newKey] = reindexed[oldKey];
          delete reindexed[oldKey];
        }
      }
      return reindexed;
    });
    setProductPages((prev) => {
      const newPages = { ...prev };
      delete newPages[removedKey];
      const reindexed = { ...newPages };
      const currentBaseProducts = baseProducts[supermarketId] || [];
      for (let i = index + 1; i < currentBaseProducts.length; i += 1) {
        const oldKey = getSearchKey(supermarketId, i);
        const newKey = getSearchKey(supermarketId, i - 1);
        if (reindexed[oldKey]) {
          reindexed[newKey] = reindexed[oldKey];
          delete reindexed[oldKey];
        }
      }
      return reindexed;
    });
    setProductLimits((prev) => {
      const newLimits = { ...prev };
      delete newLimits[removedKey];
      const reindexed = { ...newLimits };
      const currentBaseProducts = baseProducts[supermarketId] || [];
      for (let i = index + 1; i < currentBaseProducts.length; i += 1) {
        const oldKey = getSearchKey(supermarketId, i);
        const newKey = getSearchKey(supermarketId, i - 1);
        if (reindexed[oldKey]) {
          reindexed[newKey] = reindexed[oldKey];
          delete reindexed[oldKey];
        }
      }
      return reindexed;
    });
    setAllProductsForBase((prev) => {
      const newProducts = { ...prev };
      delete newProducts[removedKey];
      const reindexed = { ...newProducts };
      const currentBaseProducts = baseProducts[supermarketId] || [];
      for (let i = index + 1; i < currentBaseProducts.length; i += 1) {
        const oldKey = getSearchKey(supermarketId, i);
        const newKey = getSearchKey(supermarketId, i - 1);
        if (reindexed[oldKey]) {
          reindexed[newKey] = reindexed[oldKey];
          delete reindexed[oldKey];
        }
      }
      return reindexed;
    });
    setTotalProducts((prev) => {
      const newTotals = { ...prev };
      delete newTotals[removedKey];
      const reindexed = { ...newTotals };
      const currentBaseProducts = baseProducts[supermarketId] || [];
      for (let i = index + 1; i < currentBaseProducts.length; i += 1) {
        const oldKey = getSearchKey(supermarketId, i);
        const newKey = getSearchKey(supermarketId, i - 1);
        if (reindexed[oldKey]) {
          reindexed[newKey] = reindexed[oldKey];
          delete reindexed[oldKey];
        }
      }
      return reindexed;
    });
  };

  const handleOpenPeriodicSend = () => {
    setOpenPeriodicSend(true);
  };

  const handleClosePeriodicSend = () => {
    setOpenPeriodicSend(false);
  };

  const buildContent = () => {
    const content = [];

    Object.keys(baseProducts).forEach((supermarketId) => {
      const supermarketBases = baseProducts[supermarketId] || [];
      supermarketBases.forEach((baseData) => {
        if (baseData.baseProduct && baseData.compareProducts.length > 0) {
          content.push({
            base_product: baseData.baseProduct.id,
            comp_products: baseData.compareProducts.map((product) => product.id),
          });
        }
      });
    });

    return content;
  };

  const handleSubmitForm = async (values) => {
    try {
      const content = buildContent();

      if (content.length === 0) {
        enqueueSnackbar('Debe agregar al menos un producto base con productos a comparar', {
          variant: 'warning',
        });
        return;
      }

      const objectReport = {
        userId: account.user.id,
        title: values.title,
        dateToSend: values.dateToSend || '',
        dateFrom: '',
        dateTo: '',
        type: 'Comparativo',
        frequency: values.frequency || null,
        range: values.range || '7',
        content: JSON.stringify(content),
      };

      const response = await postCreateComparativeReport(objectReport);

      if (response.success) {
        if (values.emails && values.emails !== '') {
          const objectEmailsReports = {
            emails: values.emails.split(',').map((email) => email.trim()),
            reportId: response.data.id,
          };
          const responseEmails = await postCreateEmailReport(objectEmailsReports);
          if (responseEmails.success) {
            enqueueSnackbar(responseEmails.message, { variant: 'success' });
          } else {
            enqueueSnackbar(responseEmails.message, { variant: 'warning' });
          }
        } else {
          enqueueSnackbar(response.message, { variant: 'success' });
        }
        setSubmitSuccess(true);
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err.message || 'Error al guardar el reporte', { variant: 'error' });
    }
  };

  if (submitSuccess) {
    return <Redirect to="/administracion/reportes" />;
  }

  const currentBaseProducts = selectedSupermarket ? (baseProducts[selectedSupermarket] || []) : [];

  return (
    <Formik
      initialValues={{
        title: '',
        frequency: '',
        emails: '',
        range: '7',
        dateToSend: '',
      }}
      validationSchema={Yup.object().shape({
        title: Yup.string().max(255).required('Debe ingresar un título'),
        emails: Yup.lazy(() => {
          if (openPeriodicSend === true) {
            return Yup.string().max(255).required('Debe ingresar emails separados por coma');
          }
          return Yup.string().max(255);
        }),
        frequency: Yup.lazy(() => {
          if (openPeriodicSend === true) {
            return Yup.string().max(255).required('Debe seleccionar una frecuencia');
          }
          return Yup.string().max(255);
        }),
        range: Yup.lazy(() => {
          if (openPeriodicSend === true) {
            return Yup.string().max(255).required('Debe seleccionar un rango');
          }
          return Yup.string().max(255);
        }),
        dateToSend: Yup.lazy(() => {
          if (openPeriodicSend === true) {
            return Yup.date().required('Debe seleccionar una fecha');
          }
          return Yup.date();
        }),
      })}
      onSubmit={handleSubmitForm}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Box>
            {/* Title Input */}
            <Card className={classes.card}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="title"
                      label="Título del reporte"
                      value={values.title}
                      required
                      variant="outlined"
                      error={Boolean(touched.title && errors.title)}
                      helperText={touched.title && errors.title}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Supermercado"
                      value={selectedSupermarket}
                      onChange={(e) => handleSupermarketChange(e.target.value)}
                      variant="outlined"
                      required
                      style={{ width: '100%' }}
                    >
                      {supermarkets.map((supermarket) => (
                        <MenuItem key={supermarket.id} value={supermarket.id}>
                          {supermarket.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Product Search and Base Product Selection */}
            {selectedSupermarket && (
              <>
                {/* Render each base product with its own search */}
                {currentBaseProducts.length === 0 && (
                  <Card className={classes.card}>
                    <CardContent>
                      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleAddBaseProduct}
                          size="large"
                        >
                          Añadir producto base
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}
                {currentBaseProducts.map((baseData, baseIndex) => (
                  <Card key={baseIndex} className={classes.card} style={{ marginTop: baseIndex > 0 ? '24px' : '0' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" className={classes.subtitle}>
                          Producto Base {baseIndex + 1}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => handleRemoveBaseProduct(selectedSupermarket, baseIndex)}
                        >
                          Eliminar
                        </Button>
                      </Box>

                      <Typography variant="subtitle2" gutterBottom>
                        Buscar Productos
                      </Typography>
                      <TextField
                        fullWidth
                        label="Buscar por descripción o marca"
                        value={searchTerms[getSearchKey(selectedSupermarket, baseIndex)] || ''}
                        onChange={(e) => handleSearchChange(baseIndex, e.target.value)}
                        variant="outlined"
                        placeholder="Ingrese descripción o marca del producto"
                        style={{ marginBottom: '16px' }}
                      />

                      <Grid container spacing={3}>
                        {/* Left Column - Base Products List (50% width) */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            Seleccione producto base
                          </Typography>
                          {isSearching[getSearchKey(selectedSupermarket, baseIndex)] ? (
                            <Box display="flex" justifyContent="center" p={3}>
                              <CircularProgress />
                            </Box>
                          ) : (
                            <>
                              {(
                                allProductsForBase[getSearchKey(selectedSupermarket, baseIndex)
                                ] || []
                              ).length > 0
                                ? (
                                  <>
                                    <List className={classes.productList}>
                                      {getPaginatedProducts(baseIndex).map((product) => {
                                        // Check if this product is selected as base
                                        const isSelectedAsBase = baseData.baseProduct?.id
                                          === product.id;
                                        return (
                                          <ListItem
                                            key={`base-${baseIndex}-${product.id}`}
                                            button
                                            onClick={() => {
                                              handleSelectBaseProduct(
                                                selectedSupermarket,
                                                baseIndex,
                                                product,
                                              );
                                            }}
                                            className={`${classes.productItem} ${isSelectedAsBase ? classes.selectedProduct : ''}`}
                                          >
                                            <ListItemText
                                              primary={product.description}
                                              secondary={`Marca: ${product.brand || 'N/A'} | SKU: ${product.sku}`}
                                            />
                                          </ListItem>
                                        );
                                      })}
                                    </List>
                                    <TablePagination
                                      component="div"
                                      count={totalProducts[getSearchKey(
                                        selectedSupermarket, baseIndex,
                                      )] || 0}
                                      page={productPages[getSearchKey(
                                        selectedSupermarket, baseIndex,
                                      )] || 0}
                                      onPageChange={(e, newPage) => handleProductPageChange(
                                        baseIndex, e, newPage,
                                      )}
                                      rowsPerPage={productLimits[getSearchKey(
                                        selectedSupermarket, baseIndex,
                                      )] || 10}
                                      onRowsPerPageChange={(e) => handleProductLimitChange(
                                        baseIndex, e,
                                      )}
                                      rowsPerPageOptions={[10, 25, 50]}
                                      labelRowsPerPage="Productos por página"
                                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                                    />
                                  </>
                                ) : (
                                  <Typography variant="body2" color="textSecondary" align="center" py={3}>
                                    Busque productos para seleccionar un producto base
                                  </Typography>
                                )}
                            </>
                          )}
                        </Grid>

                        {/* Right Column - Compare Products List (50% width) */}
                        <Grid item xs={12} md={6}>
                          {baseData.baseProduct ? (
                            <Box
                              key={`compare-${baseIndex}-${baseData.baseProduct.id}`}
                              className={classes.compareProductsContainer}
                              ref={(el) => {
                                compareProductsRef.current[`${selectedSupermarket}-${baseIndex}`] = el;
                              }}
                            >
                              <Box
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant="subtitle1">
                                  Productos a comparar
                                </Typography>
                                <Typography variant="subtitle1">
                                  Total seleccionados: {baseData.compareProducts.length}
                                </Typography>
                              </Box>
                              {getTotalCompareProducts(
                                baseIndex, baseData.baseProduct.id,
                              ) > 0
                                ? (
                                  <>
                                    <List className={classes.productList}>
                                      {getPaginatedCompareProducts(
                                        baseIndex, baseData.baseProduct.id,
                                      ).map((product) => {
                                        const isSelected = baseData.compareProducts?.some(
                                          (cp) => cp.id === product.id,
                                        );
                                        return (
                                          <ListItem
                                            key={`compare-${baseIndex}-${product.id}`}
                                            button
                                            onClick={() => handleToggleCompareProduct(
                                              selectedSupermarket, baseIndex, product,
                                            )}
                                            className={classes.productItem}
                                          >
                                            <Checkbox checked={isSelected} />
                                            <ListItemText
                                              primary={product.description}
                                              secondary={`Marca: ${product.brand || 'N/A'} | SKU: ${product.sku}`}
                                            />
                                          </ListItem>
                                        );
                                      })}
                                    </List>
                                    <TablePagination
                                      component="div"
                                      count={getTotalCompareProducts(
                                        baseIndex, baseData.baseProduct.id,
                                      )}
                                      page={compareProductPage}
                                      onPageChange={handleCompareProductPageChange}
                                      rowsPerPage={compareProductLimit}
                                      onRowsPerPageChange={handleCompareProductLimitChange}
                                      rowsPerPageOptions={[10, 25, 50]}
                                      labelRowsPerPage="Productos por página"
                                      labelDisplayedRows={({ from, to, count }) => {
                                        const total = count !== -1 ? count : `más de ${to}`;
                                        return `${from}-${to} de ${total}`;
                                      }}
                                    />
                                  </>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    No hay más productos para comparar
                                  </Typography>
                                )}
                            </Box>
                          ) : (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              style={{ height: '100%', minHeight: '200px' }}
                            >
                              <Typography variant="body2" color="textSecondary" align="center">
                                Seleccione un producto base para ver los productos a comparar
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}

                {/* Add Base Product Button - shown at the bottom */}
                {selectedSupermarket && (
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddBaseProduct}
                      className={classes.actionButton}
                    >
                      Añadir otro producto base
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Action Buttons */}
            <Box
              style={{
                display: 'flex',
                justifyContent: 'end',
                marginTop: '20px',
                gap: '16px',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenPeriodicSend}
              >
                Programar envío periódico
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
              >
                Guardar reporte
              </Button>
            </Box>
          </Box>

          {/* Periodic Send Modal */}
          <Dialog open={openPeriodicSend} onClose={handleClosePeriodicSend}>
            <DialogTitle>
              <Typography>
                Programar envío periódico
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  width: '100%',
                  paddingTop: '16px',
                }}
              >
                <TextField
                  fullWidth
                  label="Frecuencia de envío"
                  name="frequency"
                  value={values.frequency}
                  select
                  variant="outlined"
                  required={openPeriodicSend}
                  className={classes.createDialogTexfield}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(touched.frequency && errors.frequency)}
                  helperText={touched.frequency && errors.frequency}
                  onBlur={handleBlur}
                  onChange={handleChange}
                >
                  <MenuItem value="1">Diaria</MenuItem>
                  <MenuItem value="7">Semanal</MenuItem>
                  <MenuItem value="30">Mensual</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="Correos electrónicos"
                  name="emails"
                  value={values.emails}
                  variant="outlined"
                  required={openPeriodicSend}
                  className={classes.createDialogTexfield}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(touched.emails && errors.emails)}
                  helperText={(touched.emails && errors.emails) || 'Separados por coma'}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="dateToSend"
                  label="Enviar a partir del"
                  value={values.dateToSend}
                  type="date"
                  variant="outlined"
                  required={openPeriodicSend}
                  className={classes.createDialogTexfield}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(touched.dateToSend && errors.dateToSend)}
                  helperText={touched.dateToSend && errors.dateToSend}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                className={classes.createDialogButtons}
                onClick={handleClosePeriodicSend}
                variant="contained"
                color="secondary"
              >
                Cancelar
              </Button>
              <Button
                className={classes.createDialogButtons}
                type="button"
                onClick={() => {
                  handleSubmit();
                  handleClosePeriodicSend();
                }}
                variant="contained"
                color="primary"
              >
                Programar y guardar reporte
              </Button>
            </DialogActions>
          </Dialog>
        </form>
      )}
    </Formik>
  );
}

export default ReportCreateForm;
